/* eslint-disable no-bitwise */
import { ThemeName } from "@kaggle/core";
import { AcceleratorType, EditorType } from "@kaggle/sdk/kernels";
import { Services } from "../../api/services";
import { JupyterStatus } from "../redux/types";
import { isGpuAccelerator } from "../types/Accelerator";

export interface Dimensions {
  [propName: string]: number | string;
}

export interface Metrics {
  [propName: string]: number | string;
}

export function uuid(length = 40): string {
  let result = "";
  for (let i = 0; i < length; i++) {
    result += ((Math.random() * 16) | 0).toString(16);
  }
  return result;
}

/** The string names for the statuses used in the legacy Kernels application. */
export type KernelLegacyStatusName =
  | "OFFLINE_MODE"
  | "UNKNOWN"
  | "INITIALIZING"
  | "REQUESTED"
  | "QUEUED"
  | "STARTING"
  | "RUNNING"
  | "ERROR"
  | "STOPPED"
  | "DOWNLOADING_DATA"
  | "LOST_HEARTBEAT"
  | "RECONNECTING"
  | "RENDERING_HTML"
  | "RESTARTING_KERNEL";

/** The integer status codes used in the legacy Kernels application. */
export enum KernelLegacyStatus {
  OFFLINE_MODE,
  UNKNOWN,
  INITIALIZING,
  REQUESTED,
  QUEUED,
  STARTING,
  RUNNING,
  ERROR,
  STOPPED,
  DOWNLOADING_DATA,
  LOST_HEARTBEAT, // Reserved, unused.
  RECONNECTING,
  RENDERING_HTML,
  RESTARTING_KERNEL, // Reserved, unused.
}

const FAILURE_TO_BEGIN_RUNNING_THRESHOLD_IN_MS = 60 * 1000;

const PREPARING_STATUSES = [
  KernelLegacyStatus.INITIALIZING,
  KernelLegacyStatus.QUEUED,
  KernelLegacyStatus.STARTING,
];

const CONNECTED_STATUSES = [
  KernelLegacyStatus.RUNNING,
  KernelLegacyStatus.RENDERING_HTML,
  KernelLegacyStatus.STOPPED,
];

const SITES_TO_PROBE: { [site: string]: string } = {
  google: "https://www.google.com/favicon.ico",
  kaggle: "https://www.kaggle.com/static/images/min.png",
};

type SiteProbeMap = typeof SITES_TO_PROBE;
type SiteProbeKey = keyof SiteProbeMap;

type Trigger = "transition-to" | "transition-from" | "interval";

export const latencyTo = (site: SiteProbeKey): Promise<number> => {
  return new Promise(resolve => {
    const start = Date.now();
    const img = document.createElement("img");
    img.onerror = () => resolve(-1);
    img.onload = () => resolve(Date.now() - start);
    img.src = `${SITES_TO_PROBE[site]}?${Date.now()}`;
  });
};

// tabIsHidden returns true if the tab is currently not in focus.
const tabIsHidden = () => {
  const hiddenKey = "hidden";
  if (hiddenKey in document) return document[hiddenKey];
  const hprops = ["webkit", "moz", "ms", "o"]
    .map(p => p + "Hidden")
    .filter(p => p in document)
    .map(p => (document as any)[p]);
  return hprops.length > 0 ? hprops[0] : false; // Assume not hidden if we couldn't find the hidden property.
};

export interface KernelTelemetryData {
  status: KernelLegacyStatus;
  kernelRunId: number | null;
  accelerator: AcceleratorType;
  isInternet: boolean | null;
  sourceType: EditorType;
  themeName: ThemeName;
  jupyterLab: boolean | null;
  errorMessage: string | null;
  jupyterProxyUrl: string | null;
}

export const kernelTelemetry = (services: Services) => () => {
  // The unique ID for this notebook (frontend) session.
  const sessionId = uuid(10);
  // Once known (not all KernelStatus carry this), the scriptRunId to report.
  let currentKernelRunId: number | null = null;
  // Once known, whether this kernel version is using a GPU.
  let currentIsGpu: boolean | null = null;
  let currentIsInternet: boolean | null = null;
  let currentStatus = KernelLegacyStatus.UNKNOWN;
  let previousStatus = KernelLegacyStatus.UNKNOWN;
  // A unique ID associated with a particular instance of a status. This is reset each
  // time the status changes, and may thus carry different values for one status value.
  let currentStatusId: string | null = null;
  let currentSourceType: EditorType | null;
  let currentStart = Date.now();
  let currentMsInStatus = 0;
  let currentThemeName = services.kernelStorageClient.readThemeName();
  let currentErrorMessage: string | null = null;
  let loggedFailureToBeginRunning: boolean = false;
  let sessionStart: number | null = null;

  const commonDimensions = () => {
    return {
      host: window.location.host,
      editor: currentSourceType, // This "editor" key is hard coded in our go relay.
      sessionId,
      kernelRunId: currentKernelRunId, // May report "null" or "undefined".
      isGpu: currentIsGpu, // Same
      isInternet: currentIsInternet,
      themeName: currentThemeName, // May report "null" if using default or "light" or "dark".
      jupyterLab: true,
    };
  };

  const trackStatus = async (trigger: Trigger) => {
    const hasTimedOut =
      PREPARING_STATUSES.includes(currentStatus) &&
      currentMsInStatus > FAILURE_TO_BEGIN_RUNNING_THRESHOLD_IN_MS;
    const event = {
      ...commonDimensions(),
      // The statusId is historically simply exported as 'id'.
      id: currentStatusId,
      notebookMsInStatus: currentMsInStatus,
      sampleTime: Date.now(),
      ...(reportConnectionStatus() && {
        notebookDisconnectedPercentage: !inConnectedStatus() ? 100 : 0,
      }),
      state: KernelLegacyStatus[currentStatus].toLowerCase(),
      previousState: KernelLegacyStatus[previousStatus].toLowerCase(),
      trigger,
      // Number required to calculate availability through Stackdriver using log-based regex and metric avg.
      availability: currentStatus === KernelLegacyStatus.RUNNING ? 1 : 0,
      // True once per kernel when it has failed to enter running after some threshold; we can make a ratio
      // of this value / transition-to "RUNNING" to determine the percent of kernels that fail to enter
      // the running state.
      failedToBeginRunning: !loggedFailureToBeginRunning && hasTimedOut ? 1 : 0,
      sessionRuntimeMs: sessionStart ? Date.now() - sessionStart : null,
      errorMessage: currentErrorMessage,
      isSessionless: true,
      tabHidden: tabIsHidden(),
    };

    loggedFailureToBeginRunning = loggedFailureToBeginRunning || hasTimedOut;

    const online = await isOnlineCached();

    services.analyticsClient.logger.report("telemetry", "notebook_status", {
      ...event,
      online,
    });
  };

  const isOnline = async () => {
    const sites = Object.keys(SITES_TO_PROBE) as SiteProbeKey[];
    let connected = false;

    await Promise.all(
      sites.map(async site => {
        const latency = await latencyTo(site);

        if (latency > 0) {
          connected = true;
        }

        services.analyticsClient.logger.report(
          "telemetry",
          "notebook_latency",
          {
            ...commonDimensions(),
            site,
            latency,
          }
        );
      })
    );

    return connected;
  };

  const isOnlineCached = memoizeWithExpiry(isOnline, 10000);

  function memoizeWithExpiry<T>(fn: () => T, ms: number): () => Promise<T> {
    let value: T | undefined;
    return async () => {
      // If the value is already loaded, return it immediately:
      if (value !== undefined) {
        return value;
      }
      // Load the value:
      value = await Promise.resolve(fn());
      // Start the timer to clear the value after we've loaded it fully:
      window.setTimeout(() => (value = undefined), ms);
      return value;
    };
  }

  const calculateTiming = () => {
    currentMsInStatus = Date.now() - currentStart;
  };

  const inConnectedStatus = () => CONNECTED_STATUSES.includes(currentStatus);

  // TODO(seb): Deprecate this in favor of rule-based alert.
  // Don't send 'notebookDisconnectedPercentage' metric for statuses with less than a 10 sec duration
  // (e.g., being queued for 3 sec is not a disconnected state and Stackdriver alerting doesn't have
  // sophisticated rules so we need to filter here)
  const reportConnectionStatus = () => currentMsInStatus > 10 * 1000;

  const hasStatus = () => currentStatus !== KernelLegacyStatus.UNKNOWN;

  const sendCurrentStatus = (trigger: Trigger) => {
    calculateTiming();

    if (!hasStatus()) {
      return;
    }
    const stateChange =
      trigger === "transition-from" || trigger === "transition-to";
    if (
      !stateChange &&
      currentStatus !== KernelLegacyStatus.RUNNING &&
      currentMsInStatus > 10 * 60 * 1000
    ) {
      // Except when the notebook is Running, stop periodically tracking status once it appears we're stuck.
      return;
    }
    trackStatus(trigger);
  };

  const sendTailOfLastStatus = () => sendCurrentStatus("transition-from");

  const sendHeadOfCurrentStatus = (data: KernelTelemetryData) => {
    previousStatus = currentStatus;
    currentSourceType = data.sourceType;
    currentStatus = data.status;
    currentKernelRunId = data.kernelRunId;
    currentIsGpu = isGpuAccelerator(data.accelerator);
    currentIsInternet = data.isInternet;
    currentThemeName = data.themeName;
    currentErrorMessage = data.errorMessage;
    currentStatusId = uuid(10) as string;
    currentStart = Date.now();
    loggedFailureToBeginRunning = false;
    sendCurrentStatus("transition-to");
  };

  const onStatusChange = (data: KernelTelemetryData) => {
    if (data.status === currentStatus) {
      return;
    }

    if (
      data.status === KernelLegacyStatus.QUEUED ||
      (data.status === KernelLegacyStatus.STARTING &&
        currentStatus !== KernelLegacyStatus.QUEUED)
    ) {
      sessionStart = Date.now();
    }

    if (data.jupyterProxyUrl) {
      SITES_TO_PROBE.jupyterproxy = `${data.jupyterProxyUrl}/static/base/images/logo.png`;
    }

    sendTailOfLastStatus();
    sendHeadOfCurrentStatus(data);
  };

  const onJupyterStatusChange = (newStatus: JupyterStatus) => {
    if (newStatus === JupyterStatus.RUNNING) {
      services.analyticsClient.logger.report(
        "telemetry",
        "notebook_jupyter_status",
        {
          ...commonDimensions(),
          timeToRunningInMs: sessionStart ? Date.now() - sessionStart : null,
        }
      );
    }
  };

  // ctor used to trigger this.
  const sendCurrentStatusIntervalId = window.setInterval(
    () => sendCurrentStatus("interval"),
    60 * 1000
  );

  const stopSendingCurrentStatus = () => {
    window.clearInterval(sendCurrentStatusIntervalId);
  };

  return {
    onJupyterStatusChange,
    onStatusChange,
    stopSendingCurrentStatus,
  };
};
